import { queryOptions } from "@tanstack/react-query";
import * as notesApi from "@/lib/api/notes";
import * as entitiesApi from "@/lib/api/entities";
import { queryKeys } from "@/lib/query-keys";
import type { EntityTypes } from "db/types";
import type { NoteComment } from "@/lib/api/notes";

type NoteEntityWithEntity = EntityTypes.SerializedNoteEntityWithEntity;

export const notesQueryOptions = (userId?: string) =>
  queryOptions({
    queryKey: queryKeys.notes.list(userId),
    queryFn: ({ signal }) => notesApi.fetchNotes({ signal }),
    enabled: !!userId,
  });

export const noteQueryOptions = (noteId: string) =>
  queryOptions({
    queryKey: queryKeys.notes.detail(noteId),
    queryFn: ({ signal }) => notesApi.fetchNote(noteId, { signal }),
  });

export const unfurlQueryOptions = (url?: string) =>
  queryOptions({
    queryKey: queryKeys.unfurl.url(url ?? ""),
    queryFn: () => (url ? notesApi.unfurlUrl(url) : Promise.resolve()),
    enabled: !!url,
  });

export const noteEntitiesQueryOptions = (noteId: string) =>
  queryOptions<NoteEntityWithEntity[]>({
    queryKey: queryKeys.notes.entities(noteId),
    queryFn: () => notesApi.fetchNoteEntities(noteId),
  });

export interface ConnectedNoteResult {
  edgeId?: string;
  note: {
    id: string;
    title?: string;
    content?: string;
  };
  direction: string;
  relationshipType: string;
  connectionType: "explicit" | "entity-based";
  sharedEntities?: { id: string; name: string }[];
  sharedEntityCount?: number;
}

export const connectedNotesQueryOptions = (
  noteId: string,
  types?: string[],
  includeEntityBased: boolean = true,
  minSharedEntities: number = 1,
) =>
  queryOptions<ConnectedNoteResult[]>({
    queryKey: queryKeys.notes.connections(noteId, {
      types,
      includeEntityBased,
      minSharedEntities,
    }),
    queryFn: async () => {
      const result = await entitiesApi.getConnectedNotes(
        noteId,
        includeEntityBased,
        minSharedEntities,
      );
      return result as ConnectedNoteResult[];
    },
  });

export const entityBasedConnectionsQueryOptions = (
  noteId: string,
  minSharedEntities: number = 1,
) =>
  queryOptions({
    queryKey: queryKeys.notes.entityConnections(noteId, minSharedEntities),
    queryFn: () => entitiesApi.getSharedEntities(noteId, minSharedEntities),
  });

export const contextualSearchQueryOptions = (params: {
  keywords?: string[];
  entityTypes?: string[];
  entityNames?: string[];
}) =>
  queryOptions({
    queryKey: queryKeys.search.contextual(params),
    queryFn: () =>
      entitiesApi.searchNotesByContext(params as Record<string, string>),
  });

export const noteCommentsQueryOptions = (noteId: string) =>
  queryOptions<NoteComment[]>({
    queryKey: queryKeys.notes.comments(noteId),
    queryFn: () => notesApi.fetchNoteComments(noteId),
  });

/**
 * @deprecated Use NoteList queries instead. This query will be removed after 2025-06-01.
 */
export const subtasksQueryOptions = (noteId: string) =>
  queryOptions({
    queryKey: queryKeys.notes.subtasks(noteId),
    queryFn: () => notesApi.getSubtasks(noteId),
    enabled: !!noteId,
  });
